import { FontWeight, Heading, Text } from '@lili/lili-frontend-common';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import {
  FirstImpressionReportClickCtaAfterAnimation,
  FirstImpressionReportStartAnimation,
} from '../../../../services/firstImpressionAnalytics.service';
import { navigateToRelevantAddMoney, VIEWS_MAP } from '../../../../router/constants';
import { PlanUpgradeFlow } from '../../../../flows/planUpgrade';
import BlueButtonLottie from '../../../../assets/lotties/Blue button_01.json';
import DashboardLottie from '../../../../assets/lotties/Dashboard_11.json';
import { CategorizationWidgetSkeleton } from '../../components/skeletons/CategorizationWidgetSkeleton';
import { FEATURE_FLAG } from '../../../../constants/general';
import { featureFlagMightBeAvailable, validateFeatureFlag } from '../../../../services/featureFlagService';
import useStore from '../../../../store';
import Lottie from '../../../../generic-components/Lottie';
import { WidgetWrapper } from '../../components/WidgetWrapper';

enum LottieModes {
  IN_ACTIVE = 'in_active',
  ACTIVE = 'active',
  UPGRADE = 'upgrade',
  EXTERNAL_BANK_ACCOUNT = 'external_bank_account',
}

interface ICategorizationWidget {
  isLoading: boolean;
}

export const CategorizationTinder = ({ isLoading }: ICategorizationWidget) => {
  const { t } = useTranslation('dashboardFaceliftResourcesEN');
  const [lottieMode, setLottieMode] = useState<LottieModes>(LottieModes.IN_ACTIVE);
  const [isPlanUpgradeFlowOpen, setIsPlanUpgradeFlowOpen] = useState(false);
  const navigate = useNavigate();
  const summaryData = useStore((state) => state.summary);

  const {
    customer: { customerProfile, nationalIdDescription },
  } = summaryData;

  const categorizationFeatureFlag = validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.FEATURE);
  const mightBeAvialaible = featureFlagMightBeAvailable(FEATURE_FLAG.CATEGORIZATION.FEATURE);
  const showUpgrade = mightBeAvialaible && !categorizationFeatureFlag;
  const isUs = !nationalIdDescription;
  useEffect(
    () => () => {
      setLottieMode(LottieModes.IN_ACTIVE);
    },
    [showUpgrade],
  );
  const handleActivateLottie = useCallback(() => {
    FirstImpressionReportStartAnimation({ customerProfilePLan: customerProfile, value: 'category_widget' });

    setLottieMode(LottieModes.ACTIVE);
  }, []);

  const handleCTAClick = useCallback(() => {
    if (lottieMode === LottieModes.ACTIVE) return;
    FirstImpressionReportClickCtaAfterAnimation({ customerProfilePLan: customerProfile, value: 'category_widget' });

    if (showUpgrade) {
      setIsPlanUpgradeFlowOpen(true);
    } else if (!isUs) {
      navigate(`/${VIEWS_MAP.INTERNATIONAL_WIRE_TRANSFER}`);
    } else {
      navigateToRelevantAddMoney(navigate);
    }
  }, [showUpgrade, lottieMode]);

  const eventListeners = [
    {
      event: 'complete',
      callback: () => {
        setLottieMode(showUpgrade ? LottieModes.UPGRADE : LottieModes.EXTERNAL_BANK_ACCOUNT);
      },
    },
  ];

  /**
   * Renders the "Watch How" button/Lottie on the inactive screen
   */
  const renderWatchHowButton = () => (
    <button
      className='w-[160px] h-[44px] flex justify-center items-center gap-1 mt-4 relative cursor-pointer'
      aria-label='Start categorization'
      type='button'
      id='watchHowButton'
      onClick={() => handleActivateLottie()}
    >
      <div className='relative flex flex-col items-center rounded-[12px]  justify-center'>
        <Lottie
          eventListeners={eventListeners}
          className='w-[120px] h-[120px] mt-4 cursor-pointer rounded-[12px]'
          isAutoplay
          isLoop
          lottieAsset={BlueButtonLottie}
        />
      </div>
    </button>
  );

  /**
   * Renders the large lottie when user has activated
   */
  const renderActiveLottie = () => (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      id='activeLottie'
      onClick={() => handleCTAClick()}
      className={twMerge(
        'w-full h-[538px] rounded-[12px] relative',
        lottieMode !== LottieModes.ACTIVE && 'cursor-pointer',
      )}
    >
      <Lottie className='' eventListeners={eventListeners} isAutoplay isLoop={false} lottieAsset={DashboardLottie} />
    </div>
  );

  /**
   * Renders the view when LottieMode is IN_ACTIVE
   */
  const renderInActiveView = () => (
    <div className='bg-surface-brand-action-05 w-full h-[538px] rounded-[12px]'>
      <div id='title' className='flex flex-col items-center justify-center h-full p-[60px]'>
        <Heading.H4 fontWeight={FontWeight.REGULAR} className='text-text-main-primary-inverse text-center w-[229px]'>
          {t('TINDER_WIDGET.TITLE')}
        </Heading.H4>
        <Heading.H4 fontWeight={FontWeight.REGULAR} className='text-text-main-primary-inverse text-center w-[229px]'>
          {t('TINDER_WIDGET.TITLE2')}
        </Heading.H4>
        <Text.H8 fontWeight={FontWeight.REGULAR} className='text-text-main-primary-inverse mt-[29px]'>
          {t('TINDER_WIDGET.WATCH_HOW')}
        </Text.H8>
        {renderWatchHowButton()}
      </div>
    </div>
  );

  /**
   * Choose which content to render based on the current LottieMode
   */
  const renderContent = () => {
    switch (lottieMode) {
      case LottieModes.ACTIVE:
        return renderActiveLottie();
      case LottieModes.IN_ACTIVE:
        return renderInActiveView();
      default:
        return renderActiveLottie();
    }
  };
  if (isLoading) {
    return (
      <WidgetWrapper className='h-[537px] row-span-2 col-span-1' testId='DashboardWidget_latest-transactions'>
        <CategorizationWidgetSkeleton />
      </WidgetWrapper>
    );
  }

  return (
    <div className='w-full  overflow-hidden h-[537px] relative flex flex-col justify-between rounded-[12px] z-49'>
      {renderContent()}
      {isPlanUpgradeFlowOpen && (
        <PlanUpgradeFlow
          onCloseClick={() => {
            setLottieMode(LottieModes.IN_ACTIVE);
            setIsPlanUpgradeFlowOpen(false);
          }}
          origin='categorization'
        />
      )}
    </div>
  );
};
